import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';

export type TreeNode = {
  id: string;
  label: string;
  type?: string;
  hasChildren?: boolean;
  metadata?: Record<string, any>;
};

export type TreeExplorerProps = {
  rootNodes: TreeNode[];
  onNodeSelect?: (node: TreeNode, path: string[]) => void;
  onLoadChildren?: (node: TreeNode, path: string[]) => Promise<TreeNode[]>;
  isLoading?: boolean;
  selectedPath?: string[];
  height?: number;
  columnWidth?: number;
  showMetadata?: boolean;
  refreshKey?: number;
  /** Enable a search/filter field above the tree columns */
  searchable?: boolean;
};

export const TreeExplorer = ({
  rootNodes,
  onNodeSelect,
  onLoadChildren,
  isLoading = false,
  selectedPath,
  height = 400,
  columnWidth = 220,
  showMetadata = true,
  refreshKey = 0,
  searchable = false,
}: TreeExplorerProps) => {
  const [path, setPath] = useState<string[]>(() => selectedPath ?? []);
  const [columnsData, setColumnsData] = useState<TreeNode[][]>([]);
  const [loadingColumns, setLoadingColumns] = useState<Set<number>>(new Set());
  const [errorColumns, setErrorColumns] = useState<Map<number, string>>(new Map());
  const [searchQuery, setSearchQuery] = useState('');

  // Refs for keyboard focus management
  const columnRefs = useRef<Map<number, HTMLElement>>(new Map());
  const [focusedColumn, setFocusedColumn] = useState(0);
  const [focusedIndex, setFocusedIndex] = useState(0);

  // Keep internal path in sync with external selectedPath (controlled mode only)
  useEffect(() => {
    if (typeof selectedPath === 'undefined') return;
    setPath((prev) => {
      const same =
        prev.length === selectedPath.length && prev.every((v, i) => v === selectedPath[i]);
      return same ? prev : selectedPath;
    });
  }, [selectedPath]);

  // Initialize first column only if empty to avoid collapsing columns on updates
  useEffect(() => {
    if (rootNodes.length === 0) return;
    setColumnsData((prev) => (prev.length === 0 ? [rootNodes] : prev));
  }, [rootNodes]);

  // Rebuild columns when path changes or when refreshKey bumps
  useEffect(() => {
    const buildColumns = async () => {
      if (rootNodes.length === 0) return;

      const newColumns: TreeNode[][] = [rootNodes];
      let currentNodes = rootNodes;

      for (let i = 0; i < path.length; i++) {
        const nodeId = path[i];
        const node = currentNodes.find((n) => n.id === nodeId);

        if (!node) {
          // Path references a node not in the current data — truncate cleanly
          break;
        }

        if (node.hasChildren && onLoadChildren) {
          const colIdx = i + 1;
          setLoadingColumns((prev) => new Set([...prev, colIdx]));
          setErrorColumns((prev) => {
            const next = new Map(prev);
            next.delete(colIdx);
            return next;
          });

          try {
            const childNodes = await onLoadChildren(node, path.slice(0, i + 1));
            currentNodes = childNodes;
            newColumns.push(childNodes);
          } catch (error) {
            console.error('Error loading children:', error);
            setErrorColumns((prev) => new Map(prev).set(colIdx, 'Failed to load items'));
            break;
          } finally {
            setLoadingColumns((prev) => {
              const newSet = new Set(prev);
              newSet.delete(colIdx);
              return newSet;
            });
          }
        } else {
          break;
        }
      }

      setColumnsData(newColumns);
    };

    buildColumns();
  }, [path, rootNodes, onLoadChildren, refreshKey]);

  const handleSelect = (level: number, node: TreeNode) => {
    const newPath = path.slice(0, level);
    newPath[level] = node.id;
    setPath(newPath);
    setFocusedColumn(level);
    // Clear search when navigating to keep context clean
    if (searchQuery) setSearchQuery('');

    if (onNodeSelect) {
      onNodeSelect(node, newPath);
    }
  };

  const retryColumn = useCallback(() => {
    // Force rebuild by re-setting path
    setPath((prev) => [...prev]);
  }, []);

  const getCurrentNode = (): TreeNode | null => {
    if (path.length === 0) return null;

    let currentNodes = rootNodes;
    let currentNode: TreeNode | null = null;

    for (let i = 0; i < path.length; i++) {
      const nodeId = path[i];
      currentNode = currentNodes.find((n) => n.id === nodeId) || null;
      if (!currentNode) break;
      if (i + 1 < columnsData.length) {
        currentNodes = columnsData[i + 1];
      }
    }

    return currentNode;
  };

  // Filter nodes based on search query — only applies to the deepest column
  // so parent/ancestor columns retain full context.
  const getFilteredNodes = (nodes: TreeNode[], level: number): TreeNode[] => {
    if (!searchQuery.trim()) return nodes;
    // Only filter the last (deepest) column
    if (level < columnsData.length - 1) return nodes;
    const q = searchQuery.toLowerCase();
    return nodes.filter((n) => n.label.toLowerCase().includes(q));
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent, level: number, allNodes: TreeNode[]) => {
    const filtered = getFilteredNodes(allNodes, level);
    const currentIdx = focusedColumn === level ? focusedIndex : 0;

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const nextIdx = Math.min(currentIdx + 1, filtered.length - 1);
        setFocusedColumn(level);
        setFocusedIndex(nextIdx);
        focusItem(level, nextIdx);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prevIdx = Math.max(currentIdx - 1, 0);
        setFocusedColumn(level);
        setFocusedIndex(prevIdx);
        focusItem(level, prevIdx);
        break;
      }
      case 'ArrowRight': {
        e.preventDefault();
        if (level + 1 < columnsData.length) {
          setFocusedColumn(level + 1);
          setFocusedIndex(0);
          focusItem(level + 1, 0);
        }
        break;
      }
      case 'ArrowLeft': {
        e.preventDefault();
        if (level > 0) {
          setFocusedColumn(level - 1);
          const parentIdx = columnsData[level - 1]?.findIndex((n) => n.id === path[level - 1]) ?? 0;
          setFocusedIndex(Math.max(parentIdx, 0));
          focusItem(level - 1, Math.max(parentIdx, 0));
        }
        break;
      }
      case 'Enter':
      case ' ': {
        e.preventDefault();
        if (filtered[currentIdx]) {
          handleSelect(level, filtered[currentIdx]);
        }
        break;
      }
    }
  };

  const focusItem = (level: number, idx: number) => {
    const col = columnRefs.current.get(level);
    if (!col) return;
    const items = col.querySelectorAll('[role="treeitem"]');
    (items[idx] as HTMLElement)?.focus();
  };

  // Don't show stale detail while loading
  const currentNode = loadingColumns.size > 0 ? null : getCurrentNode();

  if (isLoading && columnsData.length === 0) {
    return (
      <Paper
        elevation={1}
        sx={{ display: 'flex', height, alignItems: 'center', justifyContent: 'center' }}
      >
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <Box>
      {searchable && (
        <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
          <TextField
            size="small"
            fullWidth
            placeholder={
              columnsData.length > 1 ? `Filter level ${columnsData.length}…` : 'Filter items…'
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    edge="end"
                    aria-label="Clear search"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
      )}
      <Paper
        elevation={1}
        sx={{ display: 'flex', height: searchable ? height - 48 : height, overflowX: 'auto' }}
        role="tree"
        aria-label="Hierarchy explorer"
      >
        {columnsData.map((nodes, level) => {
          const filtered = getFilteredNodes(nodes, level);
          const hasError = errorColumns.has(level);

          return (
            <Box
              key={level}
              ref={(el: HTMLElement | null) => {
                if (el) columnRefs.current.set(level, el);
                else columnRefs.current.delete(level);
              }}
              role="group"
              aria-label={`Level ${level + 1}`}
              sx={{
                width: columnWidth,
                borderRight: 1,
                borderColor: 'divider',
                overflowY: 'auto',
                flexShrink: 0,
                position: 'relative',
              }}
            >
              {loadingColumns.has(level) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 1,
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}

              {hasError ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="error" gutterBottom>
                    {errorColumns.get(level)}
                  </Typography>
                  <Button size="small" startIcon={<RefreshIcon />} onClick={retryColumn}>
                    Retry
                  </Button>
                </Box>
              ) : filtered.length === 0 && !loadingColumns.has(level) ? (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery && level === columnsData.length - 1 ? 'No matches' : 'No items'}
                  </Typography>
                </Box>
              ) : (
                <List dense disablePadding>
                  {filtered.map((node, idx) => (
                    <ListItemButton
                      key={node.id}
                      role="treeitem"
                      aria-selected={path[level] === node.id}
                      aria-expanded={path[level] === node.id && node.hasChildren ? true : undefined}
                      tabIndex={focusedColumn === level && focusedIndex === idx ? 0 : -1}
                      selected={path[level] === node.id}
                      onClick={() => handleSelect(level, node)}
                      onKeyDown={(e) => handleKeyDown(e, level, nodes)}
                      sx={{
                        '&.Mui-selected': {
                          bgcolor: 'primary.light',
                          color: 'primary.contrastText',
                        },
                      }}
                    >
                      <ListItemText primary={node.label} />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          );
        })}

        {/* Detail pane */}
        {showMetadata && (
          <Box sx={{ p: 2, minWidth: 240, maxWidth: 300 }}>
            {currentNode ? (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Item:
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {currentNode.label}
                </Typography>

                {currentNode.metadata && Object.keys(currentNode.metadata).length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                      Details:
                    </Typography>
                    {Object.entries(currentNode.metadata).map(([key, value]) => (
                      <Box key={key} sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {key}:
                        </Typography>
                        <Typography variant="body2">
                          {Array.isArray(value) ? value.join(', ') : String(value)}
                        </Typography>
                      </Box>
                    ))}
                  </>
                )}
              </>
            ) : loadingColumns.size > 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                <CircularProgress size={16} />
                <Typography variant="body2">Loading…</Typography>
              </Box>
            ) : (
              <Typography variant="subtitle2" color="text.secondary">
                Select an item to view details…
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};
